import { AlertTriangle, Eye, Info, Loader2, Trash } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { SixFileUploadLimitModal } from "@components/ui/SixFileUploadLimitModal";
import {
  ACCEPTED_INCOME_AND_ID_DOCUMENT_ACCEPT,
  ACCEPTED_INCOME_AND_ID_DOCUMENT_ERROR,
  isAcceptedIncomeOrIdDocumentFile,
} from "@/lib/utils/acceptedDocumentFile";
import type { IncomeDocumentationSectionProps } from "@/features/applicant/types/components";

const MAX_UPLOAD_FILES = 6;
const FILE_ACCEPT = ACCEPTED_INCOME_AND_ID_DOCUMENT_ACCEPT;

const incomeDocTypes = [
  {
    label: "Bank statements",
    details: [
      "Must include individual deposits for all listed income sources, for the last three months.",
      "Please provide for every account that receives income from the sources provided on your application.",
      "If you have linked your bank account (above), statements do not need to be uploaded.",
    ],
  },
  {
    label: "Pay stubs",
    details: [
      "Most recently issued pay stub within the last three weeks.",
      "Applicant name must match employee name.",
      "Must include pay date/pay period, year-to-date earnings, and deductions.",
    ],
  },
  {
    label: "Confirmation of remote work role (if applicable)",
    details: [
      "Copy of job offer, pay stub with location (confirming remote role), picture of your login portal showing your work location as remote; we can also accept a copy of an email from your employer stating your work is remote.",
    ],
  },
  {
    label: "SS/VA/Disability Award Letters",
    details: [
      "Your most recent Social Security, VA, disability, etc. award letters that can document that income, if applicable.",
    ],
  },
  {
    label: "Spousal Support documentation",
    details: ["Alimony, child support, spousal support documents."],
  },
  {
    label: "W2/1099",
    details: [
      "Only required early in calendar year if bank deposits do not demonstrate income.",
    ],
  },
  {
    label: "Additional Documents",
    details: ["Any additional documents requested by Liberty Rent."],
  },
];

function DocTypeItem({ label, details }: { label: string; details: string[] }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const hide = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <li className="relative">
      <span className="inline-flex items-center gap-1.5">
        <strong>{label}</strong>
        <span
          className="relative inline-flex items-center"
          onMouseEnter={show}
          onMouseLeave={hide}
          onFocus={show}
          onBlur={hide}
        >
          <Info
            className="h-3.5 w-3.5 text-blue-400 cursor-pointer flex-shrink-0"
            aria-label={`Details for ${label}`}
          />
          {open && (
            <div
              className="absolute right-0 top-full mt-1 z-50 w-80 sm:w-96 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-lg p-3 pointer-events-auto"
              onMouseEnter={show}
              onMouseLeave={hide}
            >
              <div className="absolute -top-2 right-0 h-2 w-full" aria-hidden />
              <ul className="list-disc pl-4 space-y-1">
                {details.map((d, i) => (
                  <li
                    key={i}
                    className="text-xs text-gray-700 leading-snug whitespace-normal break-words"
                  >
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </span>
      </span>
    </li>
  );
}

function DropzoneChrome({
  showSpinner,
  inactive,
  dragOver,
  onTrigger,
  onDragOver,
  onDragLeave,
  onDrop,
  fileInputRef,
  accept,
  onChange,
  ariaLabel,
}: {
  showSpinner: boolean;
  /** Block interaction (uploading, at cap, or parent disabled). */
  inactive: boolean;
  dragOver: boolean;
  onTrigger: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  accept: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  ariaLabel: string;
}) {
  return (
    <div className="flex flex-wrap gap-4 mb-1">
      <div className="flex-1 min-w-[280px] min-h-[180px]">
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (!inactive) onTrigger();
            }
          }}
          className={`border-2 border-dashed rounded-lg text-center bg-[#fafafa] transition-colors min-h-[180px] flex flex-col items-center justify-center ${
            inactive ? "pointer-events-none opacity-70 cursor-not-allowed" : "cursor-pointer"
          } ${
            dragOver
              ? "border-[#1597FF] bg-[#f0f8ff]"
              : "border-gray-300 hover:border-[#1597FF] hover:bg-[#f0f8ff]"
          }`}
          onClick={inactive ? undefined : onTrigger}
          onDragOver={inactive ? undefined : onDragOver}
          onDragLeave={onDragLeave}
          onDrop={inactive ? undefined : onDrop}
          style={{ padding: "4.95rem" }}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="absolute opacity-0 w-0 h-0 overflow-hidden pointer-events-none"
            accept={accept}
            multiple
            onChange={onChange}
            disabled={inactive}
            aria-label={ariaLabel}
          />
          {showSpinner ? (
            <>
              <Loader2 className="w-10 h-10 text-[#1597ff] animate-spin mx-auto mb-2" />
              <span className="font-semibold text-[#1597ff]">
                Uploading file...
              </span>
            </>
          ) : (
            <>
              <span className="text-3xl text-gray-400 mb-2 block">
                <svg
                  className="w-10 h-10 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </span>
              <span className="font-semibold text-gray-800 block mb-1">
                Upload Income Documents
              </span>
              <span className="text-sm text-gray-600 block mb-1">
                Click to browse or drag and drop files here
              </span>
              <span className="text-xs text-gray-500">
                PDF, JPG, JPEG, PNG, or HEIC. You can select multiple files at
                once.
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 min-w-[280px] rounded-lg border border-[#b8d4e8] p-4 bg-[#f7fbff] min-h-[180px]">
        <div className="font-bold text-[#232946] text-base mb-3">
          Income Verification Documents We Accept:
        </div>
        <div className="ml-4">
          <ul className="list-disc pl-5 text-sm space-y-3">
            {incomeDocTypes.map((doc) => (
              <DocTypeItem
                key={doc.label}
                label={doc.label}
                details={doc.details}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export function IncomeDocumentationSection({
  isAwaiting,
  message,
  isReadOnly,
  descriptorCopy,
  omitDescriptor,
  omitOuterCard,
  hideFileList,
  filesOnly,
  showDropzone,
  onUploadFiles,
  existingUploadCount = 0,
  existingUploadNames = [],
  maxSizeMb = 5,
  dropzoneDisabled = false,
  uploading: uploadingFromParent = false,
  dragOver: dragOverLegacy,
  onTriggerFileInput,
  onDragOver: onDragOverLegacy,
  onDragLeave: onDragLeaveLegacy,
  onDrop: onDropLegacy,
  fileInputRef: fileInputRefLegacy,
  accept = FILE_ACCEPT,
  onFileSelect: onFileSelectLegacy,
  proofOfIncome,
  onPreview,
  onRemove,
}: IncomeDocumentationSectionProps) {
  const [deletingId, setDeletingId] = useState<number | string | null>(null);
  const [validationError, setValidationError] = useState("");
  const [localUploading, setLocalUploading] = useState(false);
  const [dragOverManaged, setDragOverManaged] = useState(false);
  const [sixFileLimitModalOpen, setSixFileLimitModalOpen] = useState(false);
  const managedInputRef = useRef<HTMLInputElement>(null);

  const managedMode = Boolean(showDropzone && onUploadFiles);
  const limitMb = maxSizeMb || 5;
  const atFileLimit = existingUploadCount >= MAX_UPLOAD_FILES;
  const showManagedSpinner = uploadingFromParent || localUploading;
  const managedDropzoneInactive =
    showManagedSpinner || dropzoneDisabled || atFileLimit;

  useEffect(() => {
    if (!managedMode) return;
    setValidationError("");
    setLocalUploading(false);
    if (managedInputRef.current) managedInputRef.current.value = "";
  }, [managedMode, existingUploadCount, existingUploadNames.join("|")]);

  const handleRemove = async (attachmentId: number | string) => {
    setDeletingId(attachmentId);
    try {
      await onRemove(attachmentId);
    } finally {
      setDeletingId(null);
    }
  };

  const runManagedUpload = async (fileList: FileList | File[]) => {
    if (!onUploadFiles) return;
    const selected = Array.from(fileList || []);
    if (selected.length === 0) return;
    const existingLower = new Set(
      existingUploadNames.map((n) => n.toLowerCase()),
    );
    const allowedNew = Math.max(0, MAX_UPLOAD_FILES - existingUploadCount);
    const toUpload: File[] = [];
    const seen = new Set([...existingLower]);
    let hadDuplicate = false;
    let hadOversized = false;
    let hadInvalidType = false;
    for (const f of selected) {
      if (!isAcceptedIncomeOrIdDocumentFile(f)) {
        hadInvalidType = true;
        continue;
      }
      const key = f.name.toLowerCase();
      if (seen.has(key)) {
        hadDuplicate = true;
        continue;
      }
      if (f.size / 1024 / 1024 > limitMb) {
        hadOversized = true;
        continue;
      }
      toUpload.push(f);
      seen.add(key);
    }
    if (hadInvalidType) {
      setValidationError(ACCEPTED_INCOME_AND_ID_DOCUMENT_ERROR);
      return;
    }
    if (hadDuplicate) {
      setValidationError(
        "Sorry, you can not upload the same document more than once!",
      );
    } else if (hadOversized) {
      setValidationError(
        `File Size Exceeds Allowable Limit of ${limitMb} MB.`,
      );
    }
    const hadCapTrim = toUpload.length > allowedNew;
    if (hadCapTrim) {
      setSixFileLimitModalOpen(true);
      return;
    }
    if (toUpload.length === 0 || allowedNew === 0) {
      return;
    }
    setLocalUploading(true);
    setValidationError("");
    try {
      await onUploadFiles(toUpload);
      if (managedInputRef.current) managedInputRef.current.value = "";
    } catch {
      // Parent sets message / toast
    } finally {
      setLocalUploading(false);
    }
  };

  const fileListSection =
    !hideFileList && proofOfIncome && proofOfIncome.length > 0 ? (
      <div className="p-3 pt-0 space-y-2">
        {proofOfIncome.map((file, index) => {
          const fileName = file.filename || file.name || "document";
          const uploadedBy =
            typeof file.name === "string" && file.name.trim() !== ""
              ? file.name.trim()
              : "";
          let createdAtText = "";
          if (
            file.created_at !== undefined &&
            file.created_at !== null &&
            `${file.created_at}`.trim() !== ""
          ) {
            const createdAtNum = Number(file.created_at);
            if (Number.isFinite(createdAtNum)) {
              const createdDate = new Date(
                createdAtNum > 1000000000000
                  ? createdAtNum
                  : createdAtNum * 1000,
              );
              if (!Number.isNaN(createdDate.getTime())) {
                const yyyy = createdDate.getFullYear();
                const mm = `${createdDate.getMonth() + 1}`.padStart(2, "0");
                const dd = `${createdDate.getDate()}`.padStart(2, "0");
                const hh = `${createdDate.getHours()}`.padStart(2, "0");
                const min = `${createdDate.getMinutes()}`.padStart(2, "0");
                const ss = `${createdDate.getSeconds()}`.padStart(2, "0");
                createdAtText = `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
              } else {
                createdAtText = `${file.created_at}`.trim();
              }
            } else {
              createdAtText = `${file.created_at}`.trim();
            }
          }
          const uploadedMetaText =
            uploadedBy || createdAtText
              ? `Uploaded - ${uploadedBy}${
                  uploadedBy && createdAtText ? " " : ""
                }${createdAtText}`.trim()
              : "";
          const isDeleting = deletingId === file.attachment_id;

          return (
            <div
              key={file.attachment_id ?? index}
              className={`flex items-center hover:bg-gray-50 p-2 rounded transition-colors ${
                isDeleting ? "opacity-60" : ""
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="min-w-0 max-w-[400px]">
                  <span
                    title={fileName}
                    className="text-sm text-gray-700 truncate block"
                  >
                    {fileName}
                  </span>

                  {uploadedMetaText && (
                    <span className="text-xs text-gray-500 truncate block mt-0.5">
                      {uploadedMetaText}
                    </span>
                  )}
                </div>

                {!isDeleting && (
                  <button
                    type="button"
                    onClick={() =>
                      onPreview({
                        filepath: file.filepath,
                        name: fileName,
                        attachment_id: file.attachment_id,
                      })
                    }
                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                )}

                {isAwaiting && !isReadOnly && file.attachment_id && (
                  <button
                    type="button"
                    aria-label="Remove document"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isDeleting) handleRemove(file.attachment_id!);
                    }}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    {isDeleting ? (
                      <Loader2 className="h-4 w-4 animate-spin text-red-500" />
                    ) : (
                      <Trash className="h-4 w-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    ) : null;

  if (filesOnly) {
    return fileListSection;
  }

  const showManagedDropzone =
    managedMode && showDropzone && !isReadOnly && !dropzoneDisabled;

  const showLegacyDropzone =
    !managedMode &&
    showDropzone &&
    isAwaiting &&
    !isReadOnly &&
    fileInputRefLegacy &&
    onTriggerFileInput &&
    onDragOverLegacy &&
    onDragLeaveLegacy &&
    onDropLegacy &&
    onFileSelectLegacy;

  const uploadingLegacy = uploadingFromParent;
  const busyLegacy = uploadingLegacy;

  const inner = (
    <>
      {!omitDescriptor && (descriptorCopy || isAwaiting) && (
        <p className="text-sm text-gray-600 mb-4">
          {descriptorCopy ||
            "Please upload any supporting documentation requested by Liberty Rent here."}
        </p>
      )}
      {message && (
        <div
          className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
            message.type === "success"
              ? "bg-green-50 border border-green-200 text-green-800"
              : "bg-red-50 border border-red-200 text-red-800"
          }`}
        >
          <AlertTriangle
            className={`h-4 w-4 flex-shrink-0 ${
              message.type === "success" ? "text-green-600" : "text-red-600"
            }`}
          />
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      {showManagedDropzone && (
        <>
          <DropzoneChrome
            showSpinner={showManagedSpinner}
            inactive={managedDropzoneInactive}
            dragOver={dragOverManaged}
            onTrigger={() => managedInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!managedDropzoneInactive) setDragOverManaged(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragOverManaged(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragOverManaged(false);
              if (managedDropzoneInactive) return;
              setValidationError("");
              runManagedUpload(e.dataTransfer?.files || []);
            }}
            fileInputRef={managedInputRef}
            accept={FILE_ACCEPT}
            onChange={(e) => {
              setValidationError("");
              runManagedUpload(e.target.files || []).finally(() => {
                if (managedInputRef.current) managedInputRef.current.value = "";
              });
            }}
            ariaLabel="Upload income documents"
          />
          <p className="text-xs text-gray-500 mb-4">
            Maximum {MAX_UPLOAD_FILES} files allowed.
          </p>
          {validationError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-4">
              <p className="text-sm text-red-600">{validationError}</p>
            </div>
          )}
        </>
      )}

      {showLegacyDropzone && (
        <>
          <DropzoneChrome
            showSpinner={busyLegacy}
            inactive={busyLegacy}
            dragOver={!!dragOverLegacy}
            onTrigger={onTriggerFileInput!}
            onDragOver={(e) => {
              if (!busyLegacy) onDragOverLegacy!(e);
            }}
            onDragLeave={onDragLeaveLegacy!}
            onDrop={(e) => {
              if (!busyLegacy) onDropLegacy!(e);
            }}
            fileInputRef={fileInputRefLegacy!}
            accept={accept}
            onChange={onFileSelectLegacy!}
            ariaLabel="Upload income documents"
          />
          <p className="text-xs text-gray-500 mb-4">
            Maximum {MAX_UPLOAD_FILES} files allowed.
          </p>
        </>
      )}

      {fileListSection}
    </>
  );

  const limitModal = (
    <SixFileUploadLimitModal
      isOpen={sixFileLimitModalOpen}
      onClose={() => setSixFileLimitModalOpen(false)}
    />
  );

  if (omitOuterCard) {
    return (
      <>
        {inner}
        {limitModal}
      </>
    );
  }

  return (
    <div className="mb-6 p-6 bg-white border border-gray-200 rounded-lg">
      {inner}
      {limitModal}
    </div>
  );
}
